import { useState, type FormEvent, type ReactNode } from "react"
import { ArrowRight, CheckCircle2, ClipboardList, MapPin, Waves, Wrench } from "lucide-react"
import { PageHero } from "@/components/PageHero"
import { Reveal } from "@/components/Reveal"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"

export default function StartPilot() {
  const [sent, setSent] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setLoading(true)
    setError(null)
    const form = e.currentTarget
    const fd = new FormData(form)
    const checkedValues = (name: string) => Array.from(form.querySelectorAll<HTMLInputElement>(`input[name="${name}"]:checked`)).map((el) => el.value)
    // Validate checkbox and radio groups
    const drainTypeValues = checkedValues("drain-type")
    const problemValues = checkedValues("problem")
    const locationCount = (form.querySelector<HTMLInputElement>('input[name="locations"]:checked')?.value) ?? null
    const iotInterest = (form.querySelector<HTMLInputElement>('input[name="iot-interest"]:checked')?.value) ?? null

    if (drainTypeValues.length === 0 || problemValues.length === 0 || !locationCount || !iotInterest) {
      setError("Please complete all required fields including drain type, problem, locations and IoT interest.")
      setLoading(false)
      return
    }

    const payload = {
      name: fd.get("name"),
      organisation: fd.get("organisation"),
      role: fd.get("role"),
      email: fd.get("email"),
      phone: fd.get("phone"),
      location: fd.get("location"),
      drainTypes: drainTypeValues,
      problems: problemValues,
      locationsConsidered: locationCount,
      iotInterest: iotInterest,
      timeframe: fd.get("timeframe"),
      summary: fd.get("summary"),
    }

    try {
      const res = await fetch("https://smartdrain.network/smartdrain-forms-api-1.1.0/api/forms/pilot", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })
      if (!res.ok) throw new Error(`Server responded with ${res.status}`)
      setSent(true)
      form.reset()
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <PageHero
        eyebrow="Start a Smart Drain Pilot"
        title="Test Smart Drain in your city, council area or project site."
        description="Our pilot program allows councils, utilities, developers and infrastructure partners to evaluate Smart Drain under real-world conditions — measuring litter capture, drainage performance, maintenance requirements and IoT monitoring."
      />

      <section className="px-6 py-24 sm:py-32">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <div className="grid gap-8 rounded-[2rem] border border-slate-200 bg-white p-8 sm:p-12 lg:grid-cols-[.85fr_1.15fr] dark:border-white/10 dark:bg-white/[.04]">
              <div>
                <Badge className="text-emerald-800 dark:text-emerald-300">Pilot enquiry form</Badge>
                <h2 className="mt-5 text-4xl font-semibold tracking-[-.05em] sm:text-5xl">Tell us about your site and we'll contact you to discuss the next steps.</h2>
                <p className="mt-5 leading-7 text-slate-600 dark:text-slate-400">Start with one location and define measurable objectives around litter interception, drainage performance, maintenance demand and operational visibility.</p>
                <div className="mt-8 grid gap-4">
                  {[
                    [ClipboardList, "Retrofit assessment", "We review the drain type, current problem and site constraints."],
                    [Waves, "Performance objectives", "We define how the drain, waste behaviour and maintenance outcomes should be evaluated."],
                    [MapPin, "Deployment planning", "We discuss site conditions, pilot scope and the most suitable Smart Drain configuration."],
                  ].map(([Icon, title, body]) => {
                    const I = Icon as typeof ClipboardList
                    return (
                      <div key={String(title)} className="rounded-[1.25rem] bg-slate-50 p-5 dark:bg-white/[.06]">
                        <I className="size-5 text-emerald-700 dark:text-emerald-400" />
                        <div className="mt-3 font-semibold">{String(title)}</div>
                        <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-400">{String(body)}</p>
                      </div>
                    )
                  })}
                </div>
              </div>

              <Reveal delay={0.06}>
                <Card className="border-slate-200 p-6 sm:p-8 dark:border-white/10">
                  <form onSubmit={submit} className="grid gap-8">
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-[.16em] text-slate-400">Contact details</div>
                      <div className="mt-4 grid gap-5 sm:grid-cols-2">
                        <Field label="Name *"><input required name="name" className="input" placeholder="Your name" /></Field>
                        <Field label="Organisation *"><input required name="organisation" className="input" placeholder="Council, utility, company or institution" /></Field>
                        <Field label="Position / Role *"><input required name="role" className="input" placeholder="Your role" /></Field>
                        <Field label="Email *"><input required type="email" name="email" className="input" placeholder="Work email" /></Field>
                        <Field label="Phone *"><input required name="phone" className="input" placeholder="Phone number" /></Field>
                        <Field label="City / Country *"><input required name="location" className="input" placeholder="Location" /></Field>
                      </div>
                    </div>

                    <div>
                      <div className="text-xs font-semibold uppercase tracking-[.16em] text-slate-400">About your pilot</div>
                      <div className="mt-5 grid gap-6">
                        <CheckboxGroup
                          legend="What type of drainage system do you want to trial Smart Drain in? *"
                          name="drain-type"
                          options={["Kerb / road inlet", "Grated stormwater inlet", "Open drain", "Other"]}
                        />
                        <CheckboxGroup
                          legend="What problem are you trying to address? *"
                          name="problem"
                          options={["Drain blockage", "Localised flooding", "Litter and plastic pollution", "High maintenance or cleaning costs", "Drain monitoring", "Other"]}
                        />
                        <RadioGroup
                          legend="How many locations are you considering? *"
                          name="locations"
                          options={["1–5", "6–20", "21–50", "50+"]}
                        />
                        <RadioGroup
                          legend="Are you interested in IoT monitoring? *"
                          name="iot-interest"
                          options={["Yes", "No", "Not sure"]}
                        />
                        <Field label="Tell us briefly about the site or problem *">
                          <textarea required name="summary" rows={6} className="input resize-y" placeholder="Please describe the drainage issue, location, current maintenance challenges or pilot objectives." />
                        </Field>
                        <Field label="Preferred pilot timeframe *">
                          <input required name="timeframe" className="input" placeholder="Month / Year or approximate timeframe" />
                        </Field>
                      </div>
                    </div>

                    <div className="rounded-[1.25rem] bg-emerald-50 p-5 dark:bg-emerald-400/10">
                      <div className="flex items-start gap-3">
                        <CheckCircle2 className="mt-0.5 size-5 text-emerald-700 dark:text-emerald-400" />
                        <div>
                          <div className="font-semibold text-emerald-950 dark:text-emerald-200">What happens next?</div>
                          <p className="mt-2 text-sm leading-6 text-emerald-900/80 dark:text-emerald-100/70">Our team will review your enquiry and discuss the site conditions, pilot scope, monitoring requirements and suitable Smart Drain configuration with you.</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-4">
                      <Button type="submit" size="lg" disabled={loading}>{loading ? "Sending…" : "Start a Pilot"} <ArrowRight className="size-4" /></Button>
                      {sent && <span className="text-sm text-emerald-700 dark:text-emerald-400">Your pilot enquiry has been submitted successfully. Our team will be in touch.</span>}
                      {error && <span className="text-sm text-red-600 dark:text-red-400">{error}</span>}
                    </div>
                  </form>
                </Card>
              </Reveal>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <label className="grid gap-2 text-sm font-semibold text-slate-700 dark:text-slate-300"><span>{label}</span>{children}</label>
}

function CheckboxGroup({ legend, name, options }: { legend: string; name: string; options: string[] }) {
  return (
    <fieldset className="grid gap-3">
      <legend className="text-sm font-semibold text-slate-700 dark:text-slate-300">{legend}</legend>
      <div className="grid gap-3 sm:grid-cols-2">
        {options.map((option) => (
          <label key={option} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 dark:border-white/10 dark:bg-white/[.04] dark:text-slate-300">
            <input type="checkbox" name={name} value={option} className="size-4 accent-emerald-600" />
            <span>{option}</span>
          </label>
        ))}
      </div>
    </fieldset>
  )
}

function RadioGroup({ legend, name, options }: { legend: string; name: string; options: string[] }) {
  return (
    <fieldset className="grid gap-3">
      <legend className="text-sm font-semibold text-slate-700 dark:text-slate-300">{legend}</legend>
      <div className="grid gap-3 sm:grid-cols-2">
        {options.map((option) => (
          <label key={option} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 dark:border-white/10 dark:bg-white/[.04] dark:text-slate-300">
            <input type="radio" name={name} value={option} className="size-4 accent-emerald-600" />
            <span>{option}</span>
          </label>
        ))}
      </div>
    </fieldset>
  )
}
